import React, { useState } from 'react';
import { Media, Encounter } from '@medplum/fhirtypes';
import { Center, Image, Paper, Text } from '@mantine/core';
import { Carousel } from '@mantine/carousel';
import { useMedplum } from '@medplum/react';
import '@mantine/carousel/styles.css';
import BigCarouselModal from './BigCarouselModal';
import { showNotification } from '@mantine/notifications';
import DeleteConfirmModal from './DeleteModal';
import classes from './EncounterImageCarousel.module.css';
import { useTranslation } from 'react-i18next';

interface EncounterImageCarouselProps {
  images: Media[];
  encounter?: Encounter;
  age?: string | null;
  deletable?: boolean;
}

const EncounterImageCarousel: React.FC<EncounterImageCarouselProps> = ({
  images,
  encounter,
  age,
  deletable = false,
}) => {
  const medplum = useMedplum();
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { t } = useTranslation(['common', 'images-tab']);

  // Track if delete modal is open & which image is pending deletion
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(
    null
  );

  const getUrl = (attachment: Media['content']) =>
    attachment?.url?.startsWith('http')
      ? attachment.url
      : `${medplum.getBaseUrl()}/${attachment?.url}`;

  const handleDeleteImage = async () => {
    if (pendingDeleteIndex === null) {
      return;
    }

    const mediaToDelete = images[pendingDeleteIndex];
    if (!mediaToDelete?.id) {
      return;
    }

    try {
      await medplum.deleteResource('Media', mediaToDelete.id);

      showNotification({
        title: t('deleted', { ns: 'images-tab' }),
        message: t('deleteSuccess', { ns: 'images-tab' }),
        color: 'green',
      });

      // Adjust selectedIndex to previous slide if possible
      setSelectedIndex((prev) => {
        // If deleted image is before or equal to current slide, move back by 1
        if (pendingDeleteIndex <= prev && prev > 0) {
          return prev - 1;
        }
        return prev;
      });

      setDeleteModalOpen(false);

      // If after deletion no images left, close viewer
      if (images.length <= 1) {
        setViewerOpen(false);
      }
    } catch {
      showNotification({
        title: t('error', { ns: 'common' }),
        message: t('deleteError', { ns: 'images-tab' }),
        color: 'red',
      });
    }
  };
  if (!images.length) {
    return (
      <Paper w="100%" h={80} bg="#eee" radius={8}>
        <Center>
          <Text size="xs" color="dimmed">
            {t('noImages', { ns: 'images-tab' })}
          </Text>
        </Center>
      </Paper>
    );
  }

  return (
    <>
      <Carousel
        slideSize={{ base: '60px', sm: '60px' }}
        height={80}
        align="start"
        slideGap="xs"
        withIndicators={images.length > 6}
        withControls={images.length > 6}
        controlSize={8}
        loop
      >
        {images.map((media, idx) => {
          const attachment = media.content;
          const url = getUrl(attachment);

          return url ? (
            <Center>
              <Carousel.Slide
                key={media.id}
                className={classes.carouselSlide}
                onClick={() => {
                  setSelectedIndex(idx);
                  setViewerOpen(true);
                }}
              >
                <Image
                  src={url}
                  width={60}
                  miw={60}
                  mih={60}
                  height={60}
                  radius="md"
                  alt={
                    attachment?.title ||
                    t('encounterImageAlt', { ns: 'images-tab' })
                  }
                  fit="cover"
                />
              </Carousel.Slide>
            </Center>
          ) : (
            <Center>
              <Carousel.Slide key={media.id}>
                <Paper w={60} h={60} bg="#eee" radius={8}>
                  <Center>
                    <Text size="xs" color="dimmed">
                      {t('noImages', { ns: 'images-tab' })}
                    </Text>
                  </Center>
                </Paper>
              </Carousel.Slide>
            </Center>
          );
        })}
      </Carousel>

      <BigCarouselModal
        images={images}
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        initialSlide={selectedIndex}
        getUrl={getUrl}
        encounter={encounter}
        onDelete={
          deletable
            ? (idx: number) => {
                setPendingDeleteIndex(idx);
                setDeleteModalOpen(true);
              }
            : undefined
        }
        age={age}
      />

      <DeleteConfirmModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteImage}
      />
    </>
  );
};

export default EncounterImageCarousel;
